import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import TimeInput from './TimeInput';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Loader2, Check, Clock, Plus, X, Copy, ChevronDown, ChevronRight, Trash2, CalendarPlus, FileText, Palette, MoreVertical, Lock, Tag } from 'lucide-react';
import { format, parseISO, addMinutes } from 'date-fns';
import { toast } from 'sonner';

const HIGHLIGHT_COLORS = [
  { value: null, label: 'None', bg: 'transparent', dot: 'bg-muted', row: '', pdf: '' },
  { value: 'yellow', label: 'Yellow', bg: 'bg-yellow-500/30', dot: 'bg-yellow-400', row: 'bg-yellow-500/25', pdf: '#fef08a' },
  { value: 'green', label: 'Green', bg: 'bg-emerald-500/30', dot: 'bg-emerald-400', row: 'bg-emerald-500/25', pdf: '#a7f3d0' },
  { value: 'blue', label: 'Blue', bg: 'bg-blue-500/30', dot: 'bg-blue-400', row: 'bg-blue-500/25', pdf: '#bfdbfe' },
  { value: 'red', label: 'Red', bg: 'bg-red-500/30', dot: 'bg-red-400', row: 'bg-red-500/25', pdf: '#fecaca' },
  { value: 'purple', label: 'Purple', bg: 'bg-cyan-500/30', dot: 'bg-cyan-400', row: 'bg-cyan-500/25', pdf: '#ddd6fe' },
  { value: 'orange', label: 'Orange', bg: 'bg-orange-500/30', dot: 'bg-orange-400', row: 'bg-orange-500/25', pdf: '#fed7aa' },
];

// Schedule Templates
const SCHEDULE_TEMPLATES = {
  'one-day-show': {
    name: 'One Day Show',
    description: 'Load in → Show → Strike in one day',
    items: [
      { time: '', action: 'Truck(s) arrive for load in', type: 'Trucking' },
      { time: '', action: 'Crew call & safety meeting', type: 'Crew Call' },
      { time: '', action: 'Setup', type: 'Setup' },
      { time: '', action: 'Morning break', type: 'Meal/Break' },
      { time: '', action: 'Setup continues', type: 'Setup' },
      { time: '', action: 'Tech time', type: 'Rehearsal/Tech Check' },
      { time: '', action: 'Lunch', type: 'Meal/Break' },
      { time: '', action: 'Doors open', type: 'Show' },
      { time: '', action: 'Show', type: 'Show' },
      { time: '', action: 'Afternoon break', type: 'Meal/Break' },
      { time: '', action: 'Strike', type: 'Strike' },
      { time: '', action: 'Truck(s) arrive for load out', type: 'Trucking' },
      { time: '', action: 'EOD (End of Day)', type: 'Crew Call' },
    ]
  },
  'load-in-day': {
    name: 'Load In Day',
    description: 'Full day of setup',
    items: [
      { time: '', action: 'Truck(s) arrive for load in', type: 'Trucking' },
      { time: '', action: 'Crew call & safety meeting', type: 'Crew Call' },
      { time: '', action: 'Setup', type: 'Setup' },
      { time: '', action: 'Morning break', type: 'Meal/Break' },
      { time: '', action: 'Setup continues', type: 'Setup' },
      { time: '', action: 'Lunch', type: 'Meal/Break' },
      { time: '', action: 'Setup continues', type: 'Setup' },
      { time: '', action: 'Afternoon break', type: 'Meal/Break' },
      { time: '', action: 'Setup continues', type: 'Setup' },
      { time: '', action: 'EOD (End of Day)', type: 'Crew Call' },
    ]
  },
  'show-day': {
    name: 'Show Day',
    description: 'Show day without strike',
    items: [
      { time: '', action: 'Crew call & safety meeting', type: 'Crew Call' },
      { time: '', action: 'Tech time', type: 'Rehearsal/Tech Check' },
      { time: '', action: 'Morning break', type: 'Meal/Break' },
      { time: '', action: 'Tech time', type: 'Rehearsal/Tech Check' },
      { time: '', action: 'Lunch', type: 'Meal/Break' },
      { time: '', action: 'Doors open', type: 'Show' },
      { time: '', action: 'Show', type: 'Show' },
      { time: '', action: 'Afternoon break', type: 'Meal/Break' },
      { time: '', action: 'Show continues', type: 'Show' },
      { time: '', action: 'EOD (End of Day)', type: 'Crew Call' },
    ]
  },
  'show-day-strike': {
    name: 'Show Day w/Strike',
    description: 'Show day with strike at end',
    items: [
      { time: '', action: 'Crew call & safety meeting', type: 'Crew Call' },
      { time: '', action: 'Tech time', type: 'Rehearsal/Tech Check' },
      { time: '', action: 'Morning break', type: 'Meal/Break' },
      { time: '', action: 'Tech time', type: 'Rehearsal/Tech Check' },
      { time: '', action: 'Lunch', type: 'Meal/Break' },
      { time: '', action: 'Doors open', type: 'Show' },
      { time: '', action: 'Show', type: 'Show' },
      { time: '', action: 'Afternoon break', type: 'Meal/Break' },
      { time: '', action: 'Strike', type: 'Strike' },
      { time: '', action: 'Truck(s) arrive for load out', type: 'Trucking' },
      { time: '', action: 'EOD (End of Day)', type: 'Crew Call' },
    ]
  },
};

const SCHEDULE_TYPE_OPTIONS = [
  { value: 'Trucking', label: 'Trucking' },
  { value: 'Crew Call', label: 'Crew Call' },
  { value: 'Setup', label: 'Setup' },
  { value: 'Strike', label: 'Strike' },
  { value: 'Show', label: 'Show' },
  { value: 'Rehearsal/Tech Check', label: 'Rehearsal' },
  { value: 'Meal/Break', label: 'Meal/Break' },
  { value: 'Travel', label: 'Travel' },
];

const SCHEDULE_STATUS_OPTIONS = [
  { value: 'Tentative', label: 'Tentative' },
  { value: 'Confirmed', label: 'Confirmed' },
  { value: 'Cancelled', label: 'Cancelled' },
];

const DATE_TAG_OPTIONS = ['Load in', 'Setup', 'Rehearsals', 'Show', 'Strike', 'Load out'];

const DATE_TAG_COLORS = {
  'Load in': 'border-blue-500/40 bg-blue-500/15 text-blue-300',
  'Setup': 'border-orange-500/40 bg-orange-500/15 text-orange-300',
  'Rehearsals': 'border-cyan-500/40 bg-cyan-500/15 text-cyan-300',
  'Show': 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300',
  'Strike': 'border-red-500/40 bg-red-500/15 text-red-300',
  'Load out': 'border-cyan-500/40 bg-cyan-500/15 text-cyan-300',
};

const EditableScheduleTab = ({ event, onUpdate }) => {
  const [schedule, setSchedule] = useState(event?.schedule || []);
  const [dateTags, setDateTags] = useState(event?.schedule_date_tags || {});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [expandedDates, setExpandedDates] = useState({});
  const [focusedRow, setFocusedRow] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null); // Track row being edited to defer sorting
  const [editingRowPosition, setEditingRowPosition] = useState(null); // Store the visual position when editing starts
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, item: null });
  const [showNewDayInput, setShowNewDayInput] = useState(false);
  const [newDayDate, setNewDayDate] = useState('');
  const [templateDialog, setTemplateDialog] = useState({ open: false, template: null });
  const [templateDate, setTemplateDate] = useState('');
  const [changeDateDialog, setChangeDateDialog] = useState({ open: false, oldDate: null });
  const [newDate, setNewDate] = useState('');
  const [deleteDayConfirm, setDeleteDayConfirm] = useState({ open: false, date: null });
  const inputRefs = useRef({});
  const rowRefs = useRef({}); // Track row elements for blur detection
  
  // Excel-like cell navigation state
  const [activeCell, setActiveCell] = useState(null); // { itemId, field }
  const [isEditMode, setIsEditMode] = useState(false); // triggers re-render for visual state
  const editingRef = useRef(false); // true when user is typing in a cell
  const originalValueRef = useRef(null); // stores value before editing starts

  // Sync editingRef with render state
  const setEditing = useCallback((val) => {
    editingRef.current = val;
    setIsEditMode(val);
  }, []);

  // Cell highlight: visible ring in nav mode, bold ring + bg tint in edit mode
  const cellClass = useCallback((itemId, field, base = '') => {
    const isActive = activeCell?.itemId === itemId && activeCell?.field === field;
    if (!isActive) return `${base} focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0`;
    if (isEditMode) return `${base} !ring-2 !ring-primary bg-primary/10 focus:!ring-2 focus:!ring-primary focus-visible:!ring-2 focus-visible:!ring-primary`;
    // Nav mode: use primary color to match record outline
    return `${base} !ring-2 !ring-primary/50 focus:!ring-2 focus:!ring-primary/50 focus-visible:!ring-2 focus-visible:!ring-primary/50`;
  }, [activeCell, isEditMode]);
  
  // Track pending changes and backup timer for hybrid save
  const pendingSchedule = useRef(null);
  const pendingDateTags = useRef(null);
  const backupTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      setSchedule(event?.schedule || []);
      setDateTags(event?.schedule_date_tags || {});
      // Expand all dates by default
      const dates = [...new Set((event?.schedule || []).map(item => item.date).filter(Boolean))];
      const expanded = {};
      dates.forEach(d => expanded[d] = true);
      setExpandedDates(expanded);
      isInitialMount.current = false;
    }
  }, [event?.schedule]);

  // Flush pending saves on unmount so changes aren't lost when navigating away
  useEffect(() => {
    return () => {
      if (backupTimeoutRef.current) {
        clearTimeout(backupTimeoutRef.current);
      }
      // Fire pending save synchronously via onUpdate (best-effort on unmount)
      if (pendingSchedule.current !== null || pendingDateTags.current !== null) {
        const payload = {};
        if (pendingSchedule.current !== null) payload.schedule = pendingSchedule.current;
        if (pendingDateTags.current !== null) payload.schedule_date_tags = pendingDateTags.current;
        onUpdate(payload);
      }
    };
  }, [onUpdate]);

  // Perform the actual save
  const doSave = useCallback(async () => {
    if (pendingSchedule.current === null && pendingDateTags.current === null) return;
    
    setSaving(true);
    const payload = {};
    if (pendingSchedule.current !== null) {
      payload.schedule = pendingSchedule.current;
      pendingSchedule.current = null;
    }
    if (pendingDateTags.current !== null) {
      payload.schedule_date_tags = pendingDateTags.current;
      pendingDateTags.current = null;
    }
    
    try {
      await onUpdate(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save schedule:', error);
      toast.error('Failed to save schedule');
      if (payload.schedule) pendingSchedule.current = payload.schedule;
      if (payload.schedule_date_tags) pendingDateTags.current = payload.schedule_date_tags;
    } finally {
      setSaving(false);
    }
  }, [onUpdate]);

  // Queue a change - hybrid save with backup timer
  const queueScheduleSave = useCallback((newSchedule) => {
    pendingSchedule.current = newSchedule;
    
    if (backupTimeoutRef.current) {
      clearTimeout(backupTimeoutRef.current);
    }
    
    // Backup save after 5 seconds of inactivity
    backupTimeoutRef.current = setTimeout(() => {
      doSave();
    }, 1000);
  }, [doSave]);

  // Save immediately (for blur events)
  const saveScheduleNow = useCallback(() => {
    if (backupTimeoutRef.current) {
      clearTimeout(backupTimeoutRef.current);
    }
    doSave();
  }, [doSave]);

  // Get last item for smart defaults
  const getSmartDefaults = () => {
    const sortedSchedule = [...schedule].sort((a, b) => {
      const dateCompare = (a.date || '').localeCompare(b.date || '');
      if (dateCompare !== 0) return dateCompare;
      return (a.time || '').localeCompare(b.time || '');
    });
    
    const lastItem = sortedSchedule[sortedSchedule.length - 1];
    
    let defaultDate = event?.start_date || new Date().toISOString().split('T')[0];
    let defaultTime = '08:00';
    
    if (lastItem) {
      defaultDate = lastItem.date || defaultDate;
      if (lastItem.time) {
        // Auto-increment by duration or 30 minutes
        const duration = lastItem.duration || 30;
        const [hours, minutes] = lastItem.time.split(':').map(Number);
        const baseDate = new Date(2000, 0, 1, hours, minutes);
        const newDate = addMinutes(baseDate, duration);
        defaultTime = format(newDate, 'HH:mm');
      }
    }
    
    return {
      id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + Math.random(),
      date: defaultDate,
      time: defaultTime,
      duration: null,
      action: '',
      location: '',
      schedule_type: '',
      status: 'Tentative',
      notes: '',
      call_sheet: false
    };
  };

  // Apply a template to a specific date
  const applyTemplate = () => {
    if (!templateDialog.template || !templateDate) return;
    
    const template = SCHEDULE_TEMPLATES[templateDialog.template];
    if (!template) return;
    
    const newItems = template.items.map(item => ({
      id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + Math.random(),
      date: templateDate,
      time: item.time,
      duration: item.duration,
      action: item.action,
      location: '',
      schedule_type: item.type,
      schedule_types: [item.type],
      status: 'Tentative',
      notes: '',
      call_sheet: false
    }));
    
    const newSchedule = [...schedule, ...newItems];
    setSchedule(newSchedule);
    queueScheduleSave(newSchedule);
    
    // Expand the new date group
    setExpandedDates(prev => ({ ...prev, [templateDate]: true }));
    
    // Close dialog and reset
    setTemplateDialog({ open: false, template: null });
    setTemplateDate('');
    
    toast.success(`Added "${template.name}" template for ${format(parseISO(templateDate), 'MMM d, yyyy')}`);
  };

  const openTemplateDialog = (templateKey) => {
    setTemplateDialog({ open: true, template: templateKey });
    // Default to event start date if available
    setTemplateDate(event?.start_date || '');
  };

  const addScheduleItem = () => {
    const newItem = getSmartDefaults();
    const newSchedule = [...schedule, newItem];
    setSchedule(newSchedule);
    queueScheduleSave(newSchedule);
    
    // Expand the date group and focus the new row
    setExpandedDates(prev => ({ ...prev, [newItem.date]: true }));
    setFocusedRow(newItem.id);
    
    // Focus the action input after render
    setTimeout(() => {
      const actionInput = inputRefs.current[`${newItem.id}-action`];
      if (actionInput) actionInput.focus();
    }, 100);
  };

  const addNewDay = () => {
    if (!newDayDate) return;
    
    const newItem = {
      id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + Math.random(),
      date: newDayDate,
      time: '08:00',
      duration: null,
      action: '',
      location: '',
      schedule_type: '',
      status: 'Tentative',
      notes: '',
      call_sheet: false
    };
    
    const newSchedule = [...schedule, newItem];
    setSchedule(newSchedule);
    queueScheduleSave(newSchedule);
    
    // Expand the new date group
    setExpandedDates(prev => ({ ...prev, [newDayDate]: true }));
    setFocusedRow(newItem.id);
    setShowNewDayInput(false);
    setNewDayDate('');
    
    setTimeout(() => {
      const actionInput = inputRefs.current[`${newItem.id}-action`];
      if (actionInput) actionInput.focus();
    }, 100);
  };

  const duplicateScheduleItem = (item) => {
    const newItem = {
      ...item,
      id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + Math.random(),
      // Increment time by duration
      time: (() => {
        if (item.time) {
          const duration = item.duration || 30;
          const [hours, minutes] = item.time.split(':').map(Number);
          const baseDate = new Date(2000, 0, 1, hours, minutes);
          const newDate = addMinutes(baseDate, duration);
          return format(newDate, 'HH:mm');
        }
        return item.time;
      })()
    };
    const newSchedule = [...schedule, newItem];
    setSchedule(newSchedule);
    queueScheduleSave(newSchedule);
    setFocusedRow(newItem.id);
    
    setTimeout(() => {
      const actionInput = inputRefs.current[`${newItem.id}-action`];
      if (actionInput) actionInput.focus();
    }, 100);
  };

  const updateScheduleItem = (id, field, value) => {
    const newSchedule = schedule.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        // Handle schedule_type -> schedule_types array for backend compatibility
        if (field === 'schedule_type') {
          updated.schedule_types = value ? [value] : [];
          // Clear truck_assignments when type changes away from Trucking
          if (value !== 'Trucking' && item.schedule_types?.includes('Trucking')) {
            updated.truck_assignments = [];
          }
        }
        return updated;
      }
      return item;
    });
    setSchedule(newSchedule);
    
    // For status, call_sheet, and type changes, save immediately (these affect other views)
    if (field === 'status' || field === 'call_sheet' || field === 'schedule_type') {
      pendingSchedule.current = newSchedule;
      doSave();
    } else {
      queueScheduleSave(newSchedule);
    }
  };

  const confirmDelete = (item) => {
    setDeleteConfirm({ open: true, item });
  };

  const removeScheduleItem = () => {
    if (!deleteConfirm.item) return;
    const newSchedule = schedule.filter(item => item.id !== deleteConfirm.item.id);
    setSchedule(newSchedule);
    // Save immediately on delete - don't queue, this is a destructive action
    pendingSchedule.current = newSchedule;
    doSave();
    setDeleteConfirm({ open: false, item: null });
    toast.success('Schedule item deleted');
  };

  const toggleStatus = (id) => {
    const item = schedule.find(i => i.id === id);
    if (item) {
      const newStatus = item.status === 'Confirmed' ? 'Tentative' : item.status === 'Tentative' ? 'Confirmed' : 'Tentative';
      updateScheduleItem(id, 'status', newStatus);
    }
  };

  const toggleDateExpanded = (date) => {
    setExpandedDates(prev => ({ ...prev, [date]: !prev[date] }));
  };

  // Bulk change date for all items in a day
  const changeDayDate = () => {
    if (!changeDateDialog.oldDate || !newDate) return;
    const newSchedule = schedule.map(item =>
      item.date === changeDateDialog.oldDate ? { ...item, date: newDate } : item
    );
    setSchedule(newSchedule);
    pendingSchedule.current = newSchedule;
    // Move tags to new date
    const oldTags = dateTags[changeDateDialog.oldDate];
    if (oldTags) {
      const newDateTags = { ...dateTags, [newDate]: oldTags };
      delete newDateTags[changeDateDialog.oldDate];
      setDateTags(newDateTags);
      pendingDateTags.current = newDateTags;
    }
    doSave();
    setExpandedDates(prev => {
      const updated = { ...prev };
      delete updated[changeDateDialog.oldDate];
      updated[newDate] = true;
      return updated;
    });
    setChangeDateDialog({ open: false, oldDate: null });
    setNewDate('');
    toast.success(`Moved all items to ${format(parseISO(newDate), 'MMM d, yyyy')}`);
  };

  // Duplicate all items in a day to a new date
  const duplicateDay = (sourceDate) => {
    const itemsToDuplicate = schedule.filter(item => item.date === sourceDate);
    if (itemsToDuplicate.length === 0) return;

    // Default target: next day after source
    const nextDay = new Date(sourceDate + 'T12:00:00');
    nextDay.setDate(nextDay.getDate() + 1);
    const targetDate = nextDay.toISOString().split('T')[0];

    const newItems = itemsToDuplicate.map(item => ({
      ...item,
      id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + Math.random(),
      date: targetDate,
      status: 'Tentative',
    }));

    const newSchedule = [...schedule, ...newItems];
    setSchedule(newSchedule);
    pendingSchedule.current = newSchedule;
    doSave();
    setExpandedDates(prev => ({ ...prev, [targetDate]: true }));
    toast.success(`Duplicated ${itemsToDuplicate.length} items to ${format(parseISO(targetDate), 'MMM d, yyyy')}`);
  };

  // Delete all items in a day
  const deleteDay = () => {
    if (!deleteDayConfirm.date) return;
    const dateToDelete = deleteDayConfirm.date;
    const count = schedule.filter(item => item.date === dateToDelete).length;
    const newSchedule = schedule.filter(item => item.date !== dateToDelete);
    setSchedule(newSchedule);
    pendingSchedule.current = newSchedule;
    // Clean up tags for deleted date
    if (dateTags[dateToDelete]) {
      const newDateTags = { ...dateTags };
      delete newDateTags[dateToDelete];
      setDateTags(newDateTags);
      pendingDateTags.current = newDateTags;
    }
    doSave();
    setDeleteDayConfirm({ open: false, date: null });
    toast.success(`Deleted ${count} items from ${format(parseISO(dateToDelete), 'MMM d, yyyy')}`);
  };

  // Toggle a tag on a date header
  const toggleDateTag = (date, tag) => {
    const current = dateTags[date] || [];
    const newTags = current.includes(tag)
      ? current.filter(t => t !== tag)
      : [...current, tag];
    const newDateTags = { ...dateTags, [date]: newTags };
    // Clean up empty arrays
    if (newTags.length === 0) delete newDateTags[date];
    setDateTags(newDateTags);
    pendingDateTags.current = newDateTags;
    doSave();
  };

  // Handle row blur - only trigger sort when focus leaves the entire row
  const handleRowBlur = (e, itemId) => {
    // Don't interfere during keyboard navigation
    if (isNavigatingRef.current) return;
    // Check if focus is moving to another element within the same row
    const rowElement = rowRefs.current[itemId];
    const relatedTarget = e.relatedTarget;
    
    // If focus is staying within the row, don't clear editing state
    if (rowElement && relatedTarget && rowElement.contains(relatedTarget)) {
      return;
    }
    
    // Focus left the row - clear editing state to allow sorting and save
    setEditingRowId(null);
    setEditingRowPosition(null);
    setFocusedRow(null);
    saveScheduleNow(); // Save when leaving the row
  };

  // Handle row focus - set editing state to prevent sorting
  // Also capture the visual position of the row when focus starts
  const handleRowFocus = (itemId, visualIndex) => {
    // Only set position if we're starting to edit a new row
    if (editingRowId !== itemId) {
      setEditingRowId(itemId);
      setEditingRowPosition(visualIndex);
    }
    setFocusedRow(itemId);
  };

  // Keyboard navigation - Excel-like
  const FIELD_ORDER = ['time', 'duration', 'action', 'location', 'type', 'notes'];

  const getItemsInOrder = useCallback(() => {
    const sorted = [...schedule].sort((a, b) => {
      const dateCompare = (a.date || '').localeCompare(b.date || '');
      if (dateCompare !== 0) return dateCompare;
      return (a.time || '').localeCompare(b.time || '');
    });
    return sorted;
  }, [schedule]);

  // Safe setSelectionRange - time/number inputs throw on this call
  const safeSetSelectionRange = useCallback((el, start, end) => {
    try {
      if (el && el.setSelectionRange) el.setSelectionRange(start, end);
    } catch (_) { /* time/number inputs don't support selection */ }
  }, []);

  // Used by input onFocus to set activeCell only if nav didn't already set it
  const isNavigatingRef = useRef(false);

  const selectCell = useCallback((itemId, field, startEditing = false) => {
    const item = schedule.find(i => i.id === itemId);
    if (item?.date) setExpandedDates(prev => ({ ...prev, [item.date]: true }));
    isNavigatingRef.current = true;
    setEditing(startEditing);
    setActiveCell({ itemId, field });
    setTimeout(() => {
      const el = inputRefs.current[`${itemId}-${field}`];
      if (el) {
        el.focus();
        // In nav mode, move cursor to end without selecting text
        if (!startEditing) {
          const len = el.value?.length || 0;
          safeSetSelectionRange(el, len, len);
        }
      }
      // Clear the nav guard after focus settles
      setTimeout(() => { isNavigatingRef.current = false; }, 20);
    }, 50);
  }, [schedule, setEditing, safeSetSelectionRange]);

  const navigateCell = useCallback((itemId, field, direction) => {
    const fieldIdx = FIELD_ORDER.indexOf(field);
    if (fieldIdx === -1) return;
    const items = getItemsInOrder();
    const itemIdx = items.findIndex(i => i.id === itemId);
    if (itemIdx === -1) return;
    let tId = itemId, tF = field;
    if (direction === 'right') {
      if (fieldIdx < FIELD_ORDER.length - 1) tF = FIELD_ORDER[fieldIdx + 1];
      else if (itemIdx < items.length - 1) { tId = items[itemIdx + 1].id; tF = FIELD_ORDER[0]; }
    } else if (direction === 'left') {
      if (fieldIdx > 0) tF = FIELD_ORDER[fieldIdx - 1];
      else if (itemIdx > 0) { tId = items[itemIdx - 1].id; tF = FIELD_ORDER[FIELD_ORDER.length - 1]; }
    } else if (direction === 'down') {
      if (itemIdx < items.length - 1) tId = items[itemIdx + 1].id;
    } else if (direction === 'up') {
      if (itemIdx > 0) tId = items[itemIdx - 1].id;
    }
    if (tId !== itemId || tF !== field) selectCell(tId, tF);
  }, [getItemsInOrder, selectCell]);

  const handleCellClick = useCallback((itemId, field) => {
    setEditing(true);
    setActiveCell({ itemId, field });
  }, [setEditing]);

  const handleKeyDown = (e, itemId, field) => {
    const isEditing = editingRef.current;
    if (e.key === 'Tab') {
      e.preventDefault();
      setEditing(false);
      const fieldIdx = FIELD_ORDER.indexOf(field);
      const items = getItemsInOrder();
      const itemIdx = items.findIndex(i => i.id === itemId);
      if (!e.shiftKey) {
        if (fieldIdx === FIELD_ORDER.length - 1 && itemIdx === items.length - 1) addScheduleItem();
        else navigateCell(itemId, field, 'right');
      } else { navigateCell(itemId, field, 'left'); }
      return;
    }
    if (!isEditing) {
      // NAVIGATION MODE
      if (e.key === 'ArrowRight') { e.preventDefault(); navigateCell(itemId, field, 'right'); return; }
      if (e.key === 'ArrowLeft') { e.preventDefault(); navigateCell(itemId, field, 'left'); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); navigateCell(itemId, field, 'down'); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); navigateCell(itemId, field, 'up'); return; }
      if (e.key === 'Enter' || e.key === 'F2') { e.preventDefault(); setEditing(true); setActiveCell({ itemId, field }); return; }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault(); setEditing(true);
        if (field === 'duration') updateScheduleItem(itemId, field, null);
        else if (field !== 'type') updateScheduleItem(itemId, field, '');
        setActiveCell({ itemId, field }); return;
      }
      if (e.key === 'Escape') { e.target.blur(); setActiveCell(null); setEditing(false); return; }
      // Printable char: clear and start typing
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && field !== 'type') {
        e.preventDefault(); setEditing(true);
        if (field === 'duration') { const n = parseInt(e.key); updateScheduleItem(itemId, field, isNaN(n) ? null : n); }
        else {
          updateScheduleItem(itemId, field, e.key);
          setTimeout(() => {
            const el = inputRefs.current[`${itemId}-${field}`];
            safeSetSelectionRange(el, e.key.length, e.key.length);
          }, 0);
        }
        setActiveCell({ itemId, field }); return;
      }
    } else {
      // EDIT MODE
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        setEditing(false);
        setActiveCell({ itemId, field });
        const el = inputRefs.current[`${itemId}-${field}`];
        const len = el?.value?.length || 0;
        safeSetSelectionRange(el, len, len);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setEditing(false);
        setActiveCell({ itemId, field });
        const el = inputRefs.current[`${itemId}-${field}`];
        const len = el?.value?.length || 0;
        safeSetSelectionRange(el, len, len);
        return;
      }
    }
  };

  // Group schedule items by date
  const groupedSchedule = schedule.reduce((groups, item) => {
    const date = item.date || 'No Date';
    if (!groups[date]) groups[date] = [];
    groups[date].push(item);
    return groups;
  }, {});

  // Sort dates
  const sortedDates = Object.keys(groupedSchedule).sort();

  // Sort items within each date by time, but keep editing row in place at its original position
  sortedDates.forEach(date => {
    const items = groupedSchedule[date];
    
    // First, always sort items by time to get the proper visual order
    items.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
    
    if (editingRowId && editingRowPosition !== null) {
      // Find the editing item in the sorted array
      const editingItemIndex = items.findIndex(item => item.id === editingRowId);
      
      if (editingItemIndex !== -1) {
        // Get the editing item
        const editingItem = items[editingItemIndex];
        
        // Remove the editing item from its current sorted position
        items.splice(editingItemIndex, 1);
        
        // Reinsert editing item at its ORIGINAL visual position (stored when focus started)
        // Clamp to valid range
        const insertIndex = Math.min(editingRowPosition, items.length);
        items.splice(insertIndex, 0, editingItem);
        groupedSchedule[date] = items;
      }
      // If editing item is in a different date group, items remain sorted
    }
    // If no row being edited, items are already sorted
  });

  const formatDateHeader = (dateStr) => {
    if (dateStr === 'No Date') return 'No Date';
    try {
      return format(parseISO(dateStr), 'EEEE, MMMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <Card className="border-border/50 bg-card/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Clock className="h-5 w-5 text-cyan-400" />
            Event Schedule
          </CardTitle>
          <div className="flex items-center gap-2">
            {saving && (
              <Badge variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-400">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Saving...
              </Badge>
            )}
            {saved && !saving && (
              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <Check className="h-3 w-3 mr-1" />
                Saved
              </Badge>
            )}
            {showNewDayInput ? (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={newDayDate}
                  onChange={(e) => setNewDayDate(e.target.value)}
                  className="h-8 w-36 text-xs"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addNewDay();
                    if (e.key === 'Escape') {
                      setShowNewDayInput(false);
                      setNewDayDate('');
                    }
                  }}
                />
                <Button type="button" size="sm" onClick={addNewDay} disabled={!newDayDate}>
                  Add
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => { setShowNewDayInput(false); setNewDayDate(''); }}>
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="outline" size="sm">
                      <FileText className="h-4 w-4 mr-1" /> Templates <ChevronDown className="h-3 w-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {Object.entries(SCHEDULE_TEMPLATES).map(([key, template]) => (
                      <DropdownMenuItem key={key} onClick={() => openTemplateDialog(key)}>
                        <div className="flex flex-col">
                          <span className="font-medium">{template.name}</span>
                          <span className="text-xs text-muted-foreground">{template.description}</span>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowNewDayInput(true)}>
                  <CalendarPlus className="h-4 w-4 mr-1" /> Add Day
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={addScheduleItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Sticky Header Row */}
        {schedule.length > 0 && (
          <div className="sticky top-0 z-10 bg-card border-b border-border/50 pb-2 mb-2">
            <div className="grid gap-2 px-2 text-xs font-medium text-muted-foreground uppercase tracking-wider" style={{ gridTemplateColumns: '28px 90px 52px 1fr 1fr 120px 1fr 60px 40px 70px' }}>
              <div></div>
              <div>Time</div>
              <div>Dur</div>
              <div>Action</div>
              <div>Location</div>
              <div>Type</div>
              <div>Notes</div>
              <div className="text-center">Status</div>
              <div className="text-center">Call</div>
              <div className="text-center">Actions</div>
            </div>
          </div>
        )}

        {sortedDates.length > 0 ? (
          sortedDates.map(date => (
            <Collapsible 
              key={date} 
              open={expandedDates[date] !== false}
              onOpenChange={() => toggleDateExpanded(date)}
            >
              <div className="flex items-center bg-muted/30 rounded-md">
                <CollapsibleTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="justify-start p-3 h-auto hover:bg-muted/50 shrink-0"
                  >
                    <div className="flex items-center gap-2">
                      {expandedDates[date] !== false ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      <span className="font-medium">{formatDateHeader(date)}</span>
                      <Badge variant="outline" className="ml-2">
                        {groupedSchedule[date].length} items
                      </Badge>
                    </div>
                  </Button>
                </CollapsibleTrigger>
                {/* Date Tags */}
                <div className="flex items-center gap-1 ml-1 flex-1 min-w-0 flex-wrap" onClick={(e) => e.stopPropagation()}>
                  {(dateTags[date] || []).map(tag => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className={`text-xs cursor-pointer hover:opacity-80 ${DATE_TAG_COLORS[tag] || ''}`}
                      onClick={() => toggleDateTag(date, tag)}
                      data-testid={`date-tag-${date}-${tag.replace(/\s/g, '-')}`}
                    >
                      {tag}
                      <X className="h-3 w-3 ml-1" />
                    </Badge>
                  ))}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="h-6 w-6 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                        data-testid={`add-date-tag-${date}`}
                      >
                        <Tag className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {DATE_TAG_OPTIONS.map(tag => {
                        const isActive = (dateTags[date] || []).includes(tag);
                        return (
                          <DropdownMenuItem
                            key={tag}
                            onClick={() => toggleDateTag(date, tag)}
                            className="flex items-center gap-2"
                          >
                            <div className={`w-3 h-3 rounded-sm border ${isActive ? 'bg-primary border-primary' : 'border-muted-foreground/40'}`}>
                              {isActive && <Check className="h-3 w-3 text-primary-foreground" />}
                            </div>
                            <span className={isActive ? 'font-medium' : ''}>{tag}</span>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-9 w-9 mr-1 shrink-0 hover:bg-muted/80" data-testid={`day-menu-${date}`}>
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => { setChangeDateDialog({ open: true, oldDate: date }); setNewDate(date); }}>
                      <CalendarPlus className="h-4 w-4 mr-2" />
                      Change Date
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => duplicateDay(date)}>
                      <Copy className="h-4 w-4 mr-2" />
                      Duplicate Day
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => setDeleteDayConfirm({ open: true, date })}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Day ({groupedSchedule[date].length} items)
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <CollapsibleContent className="mt-2 space-y-2">
                {groupedSchedule[date].map((item, index) => {
                  const highlightColor = HIGHLIGHT_COLORS.find(c => c.value === item.highlight) || HIGHLIGHT_COLORS[0];

                  // Flex anchor rows are synced from Flex nightly and are
                  // read-only here — the server rejects edits to them too.
                  if (item.source === 'flex') {
                    const fmtTime = (t) => {
                      if (!t) return '—';
                      const [h, m] = t.split(':').map(Number);
                      const ampm = h >= 12 ? 'PM' : 'AM';
                      const h12 = h % 12 === 0 ? 12 : h % 12;
                      return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
                    };
                    return (
                      <div
                        key={item.id}
                        className={`grid gap-2 p-2 rounded-md items-center border border-cyan-500/25 bg-cyan-500/5 ${highlightColor.row || ''}`}
                        style={{ gridTemplateColumns: '28px 90px 52px 1fr 1fr 120px 1fr 60px 40px 70px' }}
                        title="Synced from Flex — edit in Flex; this row can't be changed here"
                        data-testid={`schedule-flex-row-${item.id}`}
                      >
                        <div className="flex justify-center">
                          <Lock className="h-3.5 w-3.5 text-cyan-400" />
                        </div>
                        <div className="text-xs font-mono px-1">{fmtTime(item.time)}</div>
                        <div />
                        <div className="flex items-center gap-2 px-1">
                          <span className="text-xs font-medium">{item.action}</span>
                          <Badge variant="outline" className="text-[10px] px-1 py-0 border-cyan-500/40 bg-cyan-500/10 text-cyan-400">
                            FLEX
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground px-1">{item.location || ''}</div>
                        <div className="text-xs text-muted-foreground px-1">{item.schedule_types?.[0] || ''}</div>
                        <div className="text-xs text-muted-foreground px-1 truncate">{item.notes || ''}</div>
                        <div className="flex justify-center">
                          <Badge variant="outline" className="text-xs border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                            Conf
                          </Badge>
                        </div>
                        <div />
                        <div className="flex justify-center">
                          <span className="text-[10px] text-muted-foreground">Flex</span>
                        </div>
                      </div>
                    );
                  }

                  return (
                  <div 
                    key={item.id}
                    ref={el => rowRefs.current[item.id] = el}
                    className={`grid gap-2 p-2 rounded-md items-center ${highlightColor.row || 'bg-background/30'} ${focusedRow === item.id ? 'ring-1 ring-primary/50' : ''} ${editingRowId === item.id ? 'ring-2 ring-cyan-500/30' : ''}`}
                    style={{ gridTemplateColumns: '28px 90px 52px 1fr 1fr 120px 1fr 60px 40px 70px' }}
                    onFocus={() => handleRowFocus(item.id, index)}
                    onBlur={(e) => handleRowBlur(e, item.id)}
                  >
                    {/* Highlight Color Selector */}
                    <div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className={`w-5 h-5 rounded-full border border-border/50 cursor-pointer hover:scale-110 transition-transform ${highlightColor.dot}`}
                            data-testid={`schedule-highlight-${item.id}`}
                          />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="min-w-0 p-2">
                          <div className="flex gap-1.5">
                            {HIGHLIGHT_COLORS.map((color) => (
                              <button
                                key={color.label}
                                onClick={() => updateScheduleItem(item.id, 'highlight', color.value)}
                                className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${color.dot} ${item.highlight === color.value ? 'border-white ring-1 ring-primary' : 'border-transparent'}`}
                                title={color.label}
                              />
                            ))}
                          </div>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    {/* Time - tabbable hour/min/ampm */}
                    <div>
                      <TimeInput
                        ref={el => inputRefs.current[`${item.id}-time`] = el}
                        value={item.time || ''}
                        onChange={(val) => updateScheduleItem(item.id, 'time', val)}
                        onClick={() => handleCellClick(item.id, 'time')}
                        onFocus={() => { if (!isNavigatingRef.current && !editingRef.current) setActiveCell({ itemId: item.id, field: 'time' }); }}
                        onKeyDown={(e) => handleKeyDown(e, item.id, 'time')}
                        className={cellClass(item.id, 'time', 'bg-background/50 h-8 text-xs px-1')}
                        data-testid={`schedule-time-${item.id}`}
                      />
                    </div>

                    {/* Duration - typeable number */}
                    <div>
                      <Input
                        ref={el => inputRefs.current[`${item.id}-duration`] = el}
                        type="number"
                        min="0"
                        step="15"
                        value={item.duration ?? ''}
                        onChange={(e) => updateScheduleItem(item.id, 'duration', e.target.value ? parseInt(e.target.value) : null)}
                        onClick={() => handleCellClick(item.id, 'duration')}
                        onFocus={() => { if (!isNavigatingRef.current && !editingRef.current) setActiveCell({ itemId: item.id, field: 'duration' }); }}
                        onKeyDown={(e) => handleKeyDown(e, item.id, 'duration')}
                        placeholder=""
                        className={cellClass(item.id, 'duration', 'bg-background/50 h-8 text-xs px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none')}
                        data-testid={`schedule-duration-${item.id}`}
                      />
                    </div>

                    {/* Action - Main field */}
                    <div>
                      <Input
                        ref={el => inputRefs.current[`${item.id}-action`] = el}
                        value={item.action || ''}
                        onChange={(e) => updateScheduleItem(item.id, 'action', e.target.value)}
                        onClick={() => handleCellClick(item.id, 'action')}
                        onFocus={() => { if (!isNavigatingRef.current && !editingRef.current) setActiveCell({ itemId: item.id, field: 'action' }); }}
                        onKeyDown={(e) => handleKeyDown(e, item.id, 'action')}
                        placeholder="Action"
                        className={cellClass(item.id, 'action', 'bg-background/50 h-8 text-xs')}
                        data-testid={`schedule-action-${item.id}`}
                      />
                    </div>

                    {/* Location */}
                    <div>
                      <Input
                        ref={el => inputRefs.current[`${item.id}-location`] = el}
                        value={item.location || ''}
                        onChange={(e) => updateScheduleItem(item.id, 'location', e.target.value)}
                        onClick={() => handleCellClick(item.id, 'location')}
                        onFocus={() => { if (!isNavigatingRef.current && !editingRef.current) setActiveCell({ itemId: item.id, field: 'location' }); }}
                        onKeyDown={(e) => handleKeyDown(e, item.id, 'location')}
                        placeholder="Location"
                        className={cellClass(item.id, 'location', 'bg-background/50 h-8 text-xs')}
                        data-testid={`schedule-location-${item.id}`}
                      />
                    </div>

                    {/* Type - Single Select Dropdown */}
                    <div>
                      {(() => {
                        const currentType = item.schedule_types?.[0] || item.schedule_type || '';
                        const hasTruckingAssignments = currentType === 'Trucking' && 
                          item.truck_assignments?.some(a => a.driver_id || a.truck);
                        return (
                          <div className="relative">
                            <Select
                              value={currentType || 'none'}
                              onValueChange={(v) => {
                                updateScheduleItem(item.id, 'schedule_type', v === 'none' ? '' : v);
                                // After selection, focus notes field
                                setTimeout(() => selectCell(item.id, 'notes'), 50);
                              }}
                              disabled={hasTruckingAssignments}
                            >
                              <SelectTrigger 
                                ref={el => inputRefs.current[`${item.id}-type`] = el}
                                className={cellClass(item.id, 'type', `bg-background/50 h-8 text-xs ${hasTruckingAssignments ? 'opacity-70' : ''}`)}
                                data-testid={`schedule-type-${item.id}`}
                                title={hasTruckingAssignments ? 'Remove truck/driver assignments in Trucking view before changing type' : undefined}
                                onKeyDown={(e) => handleKeyDown(e, item.id, 'type')}
                                onFocus={() => { if (!isNavigatingRef.current) setActiveCell({ itemId: item.id, field: 'type' }); }}
                              >
                                <SelectValue placeholder="Type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Select Type</SelectItem>
                                {SCHEDULE_TYPE_OPTIONS.map((type) => (
                                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {hasTruckingAssignments && (
                              <Lock className="h-3 w-3 text-orange-400 absolute -top-1 -right-1" />
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Notes */}
                    <div>
                      <Input
                        ref={el => inputRefs.current[`${item.id}-notes`] = el}
                        value={item.notes || ''}
                        onChange={(e) => updateScheduleItem(item.id, 'notes', e.target.value)}
                        onClick={() => handleCellClick(item.id, 'notes')}
                        onFocus={() => { if (!isNavigatingRef.current && !editingRef.current) setActiveCell({ itemId: item.id, field: 'notes' }); }}
                        onKeyDown={(e) => handleKeyDown(e, item.id, 'notes')}
                        placeholder="Notes"
                        className={cellClass(item.id, 'notes', 'bg-background/50 h-8 text-xs')}
                        data-testid={`schedule-notes-${item.id}`}
                      />
                    </div>

                    {/* Status - Clickable Badge */}
                    <div className="flex justify-center">
                      <Badge 
                        variant="outline" 
                        tabIndex={0}
                        className={`cursor-pointer text-xs transition-colors ${
                          item.status === 'Confirmed' 
                            ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' 
                            : item.status === 'Cancelled'
                            ? 'border-zinc-500/30 bg-zinc-500/10 text-zinc-400 hover:bg-zinc-500/20'
                            : 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20'
                        }`}
                        onClick={() => toggleStatus(item.id)}
                        onKeyDown={(e) => {
                          if (e.key === ' ' || e.key === 'Enter') {
                            e.preventDefault();
                            toggleStatus(item.id);
                          }
                        }}
                        data-testid={`schedule-status-${item.id}`}
                      >
                        {item.status === 'Confirmed' ? 'Conf' : item.status === 'Cancelled' ? 'Canc' : 'Tent'}
                      </Badge>
                    </div>

                    {/* Call Sheet Checkbox */}
                    <div className="flex justify-center">
                      <Checkbox
                        checked={item.call_sheet || false}
                        onCheckedChange={(checked) => updateScheduleItem(item.id, 'call_sheet', checked)}
                        data-testid={`schedule-callsheet-${item.id}`}
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex justify-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7"
                        onClick={() => duplicateScheduleItem(item)}
                        title="Duplicate"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-7 w-7 hover:bg-destructive/10"
                        onClick={() => confirmDelete(item)}
                        title="Delete"
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    </div>
                  </div>
                  );
                })}
                
                {/* Quick Add for this date */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-2 text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    const newItem = getSmartDefaults();
                    newItem.date = date === 'No Date' ? '' : date;
                    const newSchedule = [...schedule, newItem];
                    setSchedule(newSchedule);
                    queueScheduleSave(newSchedule);
                    setFocusedRow(newItem.id);
                    setTimeout(() => {
                      const actionInput = inputRefs.current[`${newItem.id}-action`];
                      if (actionInput) actionInput.focus();
                    }, 100);
                  }}
                >
                  <Plus className="h-3 w-3 mr-1" /> Add to {date === 'No Date' ? 'this group' : format(parseISO(date), 'MMM d')}
                </Button>
              </CollapsibleContent>
            </Collapsible>
          ))
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">No schedule items yet</p>
            <Button onClick={addScheduleItem}>
              <Plus className="h-4 w-4 mr-2" /> Add First Item
            </Button>
          </div>
        )}
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Schedule Item</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this schedule item?
              {deleteConfirm.item && (
                <span className="block mt-2 font-medium text-foreground">
                  "{deleteConfirm.item.action || 'Untitled item'}" at {deleteConfirm.item.time || 'no time'}
                </span>
              )}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={removeScheduleItem}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Template Date Picker Dialog */}
      <Dialog open={templateDialog.open} onOpenChange={(open) => { if (!open) { setTemplateDialog({ open: false, template: null }); setTemplateDate(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Add {templateDialog.template && SCHEDULE_TEMPLATES[templateDialog.template]?.name}
            </DialogTitle>
            <DialogDescription>
              {templateDialog.template && SCHEDULE_TEMPLATES[templateDialog.template]?.description}
              <br />
              Select the date for this schedule template.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="template-date" className="text-sm font-medium mb-2 block">
              Date
            </Label>
            <Input
              id="template-date"
              type="date"
              value={templateDate}
              onChange={(e) => setTemplateDate(e.target.value)}
              className="w-full"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTemplateDialog({ open: false, template: null }); setTemplateDate(''); }}>
              Cancel
            </Button>
            <Button onClick={applyTemplate} disabled={!templateDate}>
              Add Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Day Date Dialog */}
      <Dialog open={changeDateDialog.open} onOpenChange={(open) => { if (!open) { setChangeDateDialog({ open: false, oldDate: null }); setNewDate(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Date</DialogTitle>
            <DialogDescription>
              Move all {changeDateDialog.oldDate ? groupedSchedule[changeDateDialog.oldDate]?.length || 0 : 0} items
              {changeDateDialog.oldDate ? ` from ${format(parseISO(changeDateDialog.oldDate), 'MMM d, yyyy')}` : ''} to a new date.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="new-day-date" className="text-sm font-medium mb-2 block">New Date</Label>
            <Input
              id="new-day-date"
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="w-full"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setChangeDateDialog({ open: false, oldDate: null }); setNewDate(''); }}>Cancel</Button>
            <Button onClick={changeDayDate} disabled={!newDate || newDate === changeDateDialog.oldDate}>Move Items</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Day Confirmation Dialog */}
      <AlertDialog open={deleteDayConfirm.open} onOpenChange={(open) => !open && setDeleteDayConfirm({ open: false, date: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Entire Day</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete all {deleteDayConfirm.date ? groupedSchedule[deleteDayConfirm.date]?.length || 0 : 0} items
              {deleteDayConfirm.date ? ` from ${format(parseISO(deleteDayConfirm.date), 'EEEE, MMM d, yyyy')}` : ''}?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteDay} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete All Items
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default EditableScheduleTab;
